import React from 'react';
import { MapNode } from './useMapLayout';
import { DiagnosticProbe } from '../../../types/persona_engine';
import { FiCheckCircle, FiXCircle, FiCircle, FiClock } from 'react-icons/fi';

interface Props {
  node: MapNode;
  transform: { x: number; y: number; scale: number };
  canvasWidth: number;
  canvasHeight: number;
  darkMode: boolean;
}

const FOCUS_COLORS: Record<string, { bg: string; text: string }> = {
  tethic_violation:    { bg: '#7f1d1d', text: '#fca5a5' },
  antipattern_trigger: { bg: '#7c2d12', text: '#fdba74' },
  domain_edge_case:    { bg: '#1e3a5f', text: '#93c5fd' },
  tone_stress:         { bg: '#4a1d96', text: '#c4b5fd' },
  capability_boundary: { bg: '#1e1b4b', text: '#a5b4fc' },
  safety:              { bg: '#14532d', text: '#86efac' },
};

const FOCUS_COLORS_LIGHT: Record<string, { bg: string; text: string }> = {
  tethic_violation:    { bg: '#fee2e2', text: '#991b1b' },
  antipattern_trigger: { bg: '#ffedd5', text: '#9a3412' },
  domain_edge_case:    { bg: '#dbeafe', text: '#1e40af' },
  tone_stress:         { bg: '#ede9fe', text: '#5b21b6' },
  capability_boundary: { bg: '#e0e7ff', text: '#3730a3' },
  safety:              { bg: '#dcfce7', text: '#166534' },
};

/**
 * Full-readout probe overlay at high zoom.
 * Lays out probe cards in a clean CSS grid anchored to the cluster center —
 * no d3 scatter, no overlap, no run button.
 * Each card shows: status icon + label + focus area chip + prompt preview.
 */
const ProbeClusterOverlay: React.FC<Props> = ({
  node,
  transform,
  canvasWidth,
  canvasHeight,
  darkMode,
}) => {
  if (!node.innerProbes?.length) return null;

  const toScreen = (wx: number, wy: number) => ({
    sx: wx * transform.scale + transform.x,
    sy: wy * transform.scale + transform.y,
  });

  const { sx: cx, sy: cy } = toScreen(node.x, node.y);
  const screenR = node.radius * transform.scale;

  // Viewport cull
  if (
    cx + screenR < 0 ||
    cx - screenR > canvasWidth ||
    cy + screenR < 0 ||
    cy - screenR > canvasHeight
  ) return null;

  const probes = node.innerProbes;
  const CARD_W = Math.max(140, Math.min(200, screenR * 0.85));
  const CARD_H = 72;
  const GAP = 8;
  const COLS = Math.max(1, Math.min(3, Math.ceil(Math.sqrt(probes.length))));
  const ROWS = Math.ceil(probes.length / COLS);
  const gridW = COLS * CARD_W + (COLS - 1) * GAP;
  const gridH = ROWS * CARD_H + (ROWS - 1) * GAP;

  return (
    <div
      style={{
        position: 'absolute',
        left: cx - gridW / 2,
        top: cy - gridH / 2,
        width: gridW,
        pointerEvents: 'none',
        zIndex: 20,
        display: 'grid',
        gridTemplateColumns: `repeat(${COLS}, ${CARD_W}px)`,
        gap: GAP,
      }}
    >
      {probes.map((ip) => {
        const probe: DiagnosticProbe = ip.data;

        const passed   = probe.last_passed === true;
        const failed   = probe.last_passed === false;
        const runCount = probe.run_history?.length ?? 0;

        const borderColor = passed
          ? '#22c55e'
          : failed
            ? '#ef4444'
            : darkMode ? '#4b5563' : '#d1d5db';

        const bgColor = passed
          ? darkMode ? 'rgba(20,83,45,0.55)' : 'rgba(220,252,231,0.9)'
          : failed
            ? darkMode ? 'rgba(127,29,29,0.55)' : 'rgba(254,226,226,0.9)'
            : darkMode ? 'rgba(31,41,55,0.9)' : 'rgba(255,255,255,0.95)';

        const focusStyle = probe.focus_area
          ? darkMode
            ? FOCUS_COLORS[probe.focus_area] ?? { bg: '#374151', text: '#9ca3af' }
            : FOCUS_COLORS_LIGHT[probe.focus_area] ?? { bg: '#f3f4f6', text: '#6b7280' }
          : null;

        const promptPreview = probe.input_prompt.length > 55
          ? probe.input_prompt.slice(0, 55) + '…'
          : probe.input_prompt;

        return (
          <div
            key={ip.id}
            style={{
              width: CARD_W,
              height: CARD_H,
              border: `1.5px solid ${borderColor}`,
              borderRadius: 8,
              background: bgColor,
              boxShadow: passed
                ? '0 0 6px rgba(34,197,94,0.25)'
                : failed
                  ? '0 0 6px rgba(239,68,68,0.25)'
                  : '0 2px 6px rgba(0,0,0,0.15)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Top accent bar — status color */}
            <div style={{
              height: 3,
              flexShrink: 0,
              backgroundColor: passed ? '#22c55e' : failed ? '#ef4444' : '#6b7280',
            }} />

            <div style={{ padding: '5px 8px', display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minHeight: 0 }}>
              {/* Row 1: status icon + label + run count */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
                {passed
                  ? <FiCheckCircle size={11} style={{ color: '#22c55e', flexShrink: 0 }} />
                  : failed
                    ? <FiXCircle size={11} style={{ color: '#ef4444', flexShrink: 0 }} />
                    : <FiCircle size={11} style={{ color: '#6b7280', flexShrink: 0 }} />
                }
                <span style={{
                  fontSize: 9,
                  fontWeight: 700,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                  color: darkMode ? '#f3f4f6' : '#111827',
                }}>
                  {probe.label}
                </span>
                {runCount > 0 && (
                  <span style={{
                    fontSize: 7,
                    flexShrink: 0,
                    color: darkMode ? '#6b7280' : '#9ca3af',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}>
                    <FiClock size={7} />
                    {runCount}
                  </span>
                )}
              </div>

              {/* Row 2: focus area chip */}
              {focusStyle && probe.focus_area && (
                <div style={{
                  alignSelf: 'flex-start',
                  padding: '1px 5px',
                  borderRadius: 99,
                  fontSize: 7,
                  fontWeight: 600,
                  backgroundColor: focusStyle.bg,
                  color: focusStyle.text,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  maxWidth: '100%',
                  textOverflow: 'ellipsis',
                }}>
                  {probe.focus_area.replace(/_/g, ' ')}
                </div>
              )}

              {/* Row 3: prompt preview */}
              <p style={{
                fontSize: 7.5,
                color: darkMode ? '#9ca3af' : '#6b7280',
                fontStyle: 'italic',
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                margin: 0,
                lineHeight: 1.35,
              }}>
                "{promptPreview}"
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ProbeClusterOverlay;
